import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Stack,
  Alert,
  Chip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  FormHelperText,
  Avatar,
  LinearProgress,
  Slide,
  Dialog,
  Fade,
} from '@mui/material';
import {
  Save,
  Preview,
  CloudUpload,
  ArrowBack,
  HelpOutline,
  Public,
  PublicOff,
  QrCode,
  Animation,
  ViewModule,
  ViewQuilt,
  Info,
  CheckCircle,
  Visibility,
  VisibilityOff,
  Settings,
  TrendingUp,
  Check,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { useQuery, useMutation } from 'react-query';
import { useDropzone } from 'react-dropzone';
import API from '../../../API';
import useAuth from '../../../Hooks/use-auth';

const COLOR_TEMPLATES = [
  { name: 'Ocean Breeze', main: '#6F72FF', accent: '#BA88FF', bg: '#F8FAFF' },
  { name: 'Sunset Vibes', main: '#FF5733', accent: '#C70039', bg: '#FFF8F6' },
  { name: 'Forest Green', main: '#2E7D32', accent: '#81C784', bg: '#F1F8E9' },
  { name: 'Royal Purple', main: '#7B1FA2', accent: '#CE93D8', bg: '#F3E5F5' },
  { name: 'Electric Blue', main: '#1E88E5', accent: '#42A5F5', bg: '#E3F2FD' },
  { name: 'Warm Orange', main: '#FB8C00', accent: '#FFB74D', bg: '#FFF3E0' },
  { name: 'Rose Gold', main: '#E91E63', accent: '#F8BBD9', bg: '#FCE4EC' },
  { name: 'Midnight', main: '#263238', accent: '#607D8B', bg: '#ECEFF1' },
];

const ANIMATION_TYPES = [
  { value: 'FADE_IN', label: 'Fade In', icon: '✨', description: 'Smooth fade-in effect' },
  { value: 'SLIDE_UP', label: 'Slide Up', icon: '⬆️', description: 'Slides up from bottom' },
  { value: 'BOUNCE_IN', label: 'Bounce In', icon: '🎾', description: 'Bouncy entrance effect' },
  { value: 'ZOOM_IN', label: 'Zoom In', icon: '🔍', description: 'Scales in with zoom' },
];

const FILE_SIZE_LIMIT = 4.5 * 1024 * 1024;

const MobileCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: `0 2px 16px ${alpha(theme.palette.common.black, 0.08)}`,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  margin: theme.spacing(1),
  transition: 'transform 0.1s ease',
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const MediaUploadZoneMobile = styled(Box)(({ theme, isDragActive, hasMedia }) => ({
  border: hasMedia
    ? `2px solid ${theme.palette.success.main}`
    : `2px dashed ${isDragActive ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  textAlign: 'center',
  backgroundColor: hasMedia
    ? alpha(theme.palette.success.main, 0.05)
    : isDragActive
    ? alpha(theme.palette.primary.main, 0.05)
    : alpha(theme.palette.background.paper, 0.5),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:active': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderColor: theme.palette.primary.main,
  },
}));

const ColorTemplate = styled(Box)(({ theme, isSelected }) => ({
  width: 60,
  height: 40,
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  border: isSelected
    ? `3px solid ${theme.palette.primary.main}`
    : `2px solid ${alpha(theme.palette.divider, 0.2)}`,
  transition: 'all 0.3s ease',
  position: 'relative',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  '&::after': isSelected
    ? {
        content: '"✓"',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: theme.palette.common.white,
        fontWeight: 'bold',
      }
    : {},
}));

const ModernButton = styled(Button)(({ theme, bgcolor }) => ({
  borderRadius: theme.spacing(1.5),
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(1.5, 3),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: bgcolor ? `linear-gradient(135deg, ${bgcolor}, ${alpha(bgcolor, 0.8)})` : 'inherit',
  '&:hover': {
    background: bgcolor
      ? `linear-gradient(135deg, ${alpha(bgcolor, 0.9)}, ${alpha(bgcolor, 0.7)})`
      : 'inherit',
    transform: 'translateY(-2px)',
  },
}));

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pb: 2, pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function AgencyRosterMobileSettings() {
  const theme = useTheme();
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const agencyName = userInfo?.company_name;

  const [currentTab, setCurrentTab] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [settingsExists, setSettingsExists] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    logo: '',
    viewType: 'SIMPLE',
    animationsEnabled: true,
    animationType: 'FADE_IN',
    headerMediaUrl: '',
    colorScheme: {
      main: '#6F72FF',
      accent: '#BA88FF',
      backgroundColor: '#F8FAFF',
    },
    isPublic: true,
    qrEnabled: false,
    showStats: true,
    showContactInfo: false,
    seoTitle: '',
    seoDescription: '',
    customCss: '',
  });

  // Check if form data has changed
  useEffect(() => {
    if (originalData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasChanges(changed);
    }
  }, [formData, originalData]);

  useEffect(() => {
    if (agencyName && !formData.name) {
      console.log('📱 Setting default form data for agency:', agencyName);
      const defaults = {
        name: `Creator Roster by: ${agencyName}`,
        slug: agencyName.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        description: 'A curated list of creators and all their social stats.',
        logo: '',
        viewType: 'SIMPLE',
        animationsEnabled: true,
        animationType: 'FADE_IN',
        headerMediaUrl: '',
        colorScheme: {
          main: '#6F72FF',
          accent: '#BA88FF',
          backgroundColor: '#F8FAFF',
        },
        isPublic: true,
        qrEnabled: false,
        showStats: true,
        showContactInfo: false,
        seoTitle: '',
        seoDescription: '',
        customCss: '',
      };
      setFormData(defaults);
      setOriginalData(defaults);
    }
  }, [agencyName]);

  const { isLoading } = useQuery(
    ['rosterSettings', agencyName],
    () => API.agency.getRosterSettings(agencyName),
    {
      enabled: !!agencyName,
      retry: false,
      onSuccess: (data) => {
        console.log('✅ Mobile: Loaded settings:', data);
        setSettingsExists(true);
        const loadedData = {
          name: data.name || `Creator Roster by: ${agencyName}`,
          slug: data.slug || '',
          description: data.description || 'A curated list of creators and all their social stats.',
          logo: data.logo || '',
          viewType: data.view_type || 'SIMPLE',
          animationsEnabled: data.animations_enabled !== false,
          animationType: data.animation_type || 'FADE_IN',
          headerMediaUrl: data.header_media_url || '',
          colorScheme: data.color_scheme || {
            main: '#6F72FF',
            accent: '#BA88FF',
            backgroundColor: '#F8FAFF',
          },
          isPublic: data.is_public !== false,
          qrEnabled: data.qr_enabled || false,
          showStats: data.show_stats !== false,
          showContactInfo: data.show_contact_info || false,
          seoTitle: data.seo_title || '',
          seoDescription: data.seo_description || '',
          customCss: data.custom_css || '',
        };
        setFormData(loadedData);
        setOriginalData(JSON.parse(JSON.stringify(loadedData)));

        const matchingTemplate = COLOR_TEMPLATES.findIndex(
          (tpl) =>
            tpl.main === data.color_scheme?.main &&
            tpl.accent === data.color_scheme?.accent &&
            tpl.bg === data.color_scheme?.backgroundColor
        );
        if (matchingTemplate !== -1) {
          setSelectedTemplate(matchingTemplate);
        }
        console.log('✅ Mobile: Form data updated');
      },
      onError: (err) => {
        if (err?.response?.status === 404) {
          console.log('📱 No existing settings, using defaults');
          setSettingsExists(false);
        } else {
          setErrorMsg('Failed to fetch roster settings.');
        }
      },
    }
  );

  const { mutate: uploadFile, isLoading: isUploading } = useMutation(
    (fileData) => API.files.post(fileData),
    {
      onSuccess: (data, variables) => {
        const field = variables.field;
        console.log(`✅ Mobile: ${field} uploaded:`, data.url);
        setFormData((prev) => ({ ...prev, [field]: data.url }));
        setSuccessMsg(`${field === 'logo' ? 'Logo' : 'Header media'} uploaded successfully!`);
        setTimeout(() => setSuccessMsg(''), 3000);
      },
      onError: (error) => {
        console.error('❌ Mobile: Upload error:', error);
        setErrorMsg(`Error uploading file: ${error.message}`);
      },
    }
  );

  const createMutation = useMutation(
    (payload) => API.agency.createRosterSettings(agencyName, payload),
    {
      onSuccess: (data) => {
        setSuccessMsg('Roster settings created successfully!');
        setSettingsExists(true);
        const newOriginal = JSON.parse(JSON.stringify(formData));
        setOriginalData(newOriginal);
        setHasChanges(false);
        setTimeout(() => setSuccessMsg(''), 3000);
      },
      onError: (err) => {
        setErrorMsg(err?.response?.data?.error || 'Failed to create roster settings.');
      },
    }
  );

  const updateMutation = useMutation(
    (payload) => API.agency.updateRosterSettings(agencyName, payload),
    {
      onSuccess: () => {
        setSuccessMsg('Roster settings updated successfully!');
        const newOriginal = JSON.parse(JSON.stringify(formData));
        setOriginalData(newOriginal);
        setHasChanges(false);
        setTimeout(() => setSuccessMsg(''), 3000);
      },
      onError: (err) => {
        setErrorMsg(err?.response?.data?.error || 'Failed to update roster settings.');
      },
    }
  );

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const updateColorScheme = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      colorScheme: { ...prev.colorScheme, [field]: value },
    }));
    setSelectedTemplate(null);
  };

  const logoDropHandler = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size > FILE_SIZE_LIMIT) {
      setErrorMsg('File size exceeds the 4.5MB limit. Please upload a smaller file.');
      return;
    }

    console.log('📱 Uploading logo:', file.name);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64File = reader.result.split(',')[1];
      uploadFile({
        field: 'logo',
        file: {
          name: file.name,
          content: base64File,
          type: file.type,
        },
      });
    };
  }, [uploadFile]);

  const headerDropHandler = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size > FILE_SIZE_LIMIT) {
      setErrorMsg('File size exceeds the 4.5MB limit. Please upload a smaller file.');
      return;
    }

    console.log('📱 Uploading header media:', file.name);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64File = reader.result.split(',')[1];
      uploadFile({
        field: 'headerMediaUrl',
        file: {
          name: file.name,
          content: base64File,
          type: file.type,
        },
      });
    };
  }, [uploadFile]);

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps, isDragActive: isLogoDragActive } = useDropzone({
    onDrop: logoDropHandler,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'] },
  });

  const { getRootProps: getHeaderRootProps, getInputProps: getHeaderInputProps, isDragActive: isHeaderDragActive } = useDropzone({
    onDrop: headerDropHandler,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'], 'video/*': ['.mp4', '.webm'] },
  });

  const handleSave = () => {
    setErrors({});
    setErrorMsg('');

    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.slug.trim()) newErrors.slug = 'URL slug is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setCurrentTab(0);
      return;
    }

    const payload = {
      name: formData.name,
      slug: formData.slug,
      description: formData.description,
      logo: formData.logo,
      view_type: formData.viewType,
      animations_enabled: formData.animationsEnabled,
      animation_type: formData.animationType,
      header_media_url: formData.headerMediaUrl,
      color_scheme: formData.colorScheme,
      is_public: formData.isPublic,
      qr_enabled: formData.qrEnabled,
      show_stats: formData.showStats,
      show_contact_info: formData.showContactInfo,
      seo_title: formData.seoTitle,
      seo_description: formData.seoDescription,
      custom_css: formData.customCss,
    };

    console.log('📱 Mobile: Saving payload:', JSON.stringify(payload, null, 2));

    if (!settingsExists) {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate(payload);
    }
  };

  const applyColorTemplate = (template, index) => {
    setFormData((prev) => ({
      ...prev,
      colorScheme: {
        main: template.main,
        accent: template.accent,
        backgroundColor: template.bg,
      },
    }));
    setSelectedTemplate(index);
  };

  const renderBasicSettings = () => (
    <Stack spacing={3} sx={{ p: 2 }}>
      <Typography variant="h6" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Info color="primary" />
        Basic Information
      </Typography>

      <TextField
        label="Roster Name"
        value={formData.name}
        onChange={(e) => updateField('name', e.target.value)}
        fullWidth
        error={!!errors.name}
        helperText={errors.name || 'The main title for your creator roster'}
        placeholder="Amazing Creator Roster"
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      />

      <TextField
        label="URL Slug"
        value={formData.slug}
        onChange={(e) => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        fullWidth
        error={!!errors.slug}
        helperText={errors.slug || `Your roster will be available at: /roster/${formData.slug || 'your-slug'}`}
        placeholder="my-creators"
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      />

      <TextField
        label="Description"
        value={formData.description}
        onChange={(e) => updateField('description', e.target.value)}
        fullWidth
        multiline
        rows={3}
        helperText="Brief description of your creator roster"
        placeholder="Meet our talented creators..."
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      />

      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          Logo Upload
        </Typography>
        <MediaUploadZoneMobile {...getLogoRootProps()} isDragActive={isLogoDragActive} hasMedia={!!formData.logo}>
          <input {...getLogoInputProps()} />
          {formData.logo ? (
            <Box>
              <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>
                Change Logo
              </Typography>
            </Box>
          ) : (
            <Box>
              <CloudUpload sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>
                {isLogoDragActive ? 'Drop logo here...' : 'Upload Logo'}
              </Typography>
            </Box>
          )}
        </MediaUploadZoneMobile>
        {formData.logo && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <img src={formData.logo} alt="Logo Preview" style={{ maxWidth: '150px', maxHeight: '60px', borderRadius: '8px' }} />
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          Header Banner
        </Typography>
        <MediaUploadZoneMobile {...getHeaderRootProps()} isDragActive={isHeaderDragActive} hasMedia={!!formData.headerMediaUrl}>
          <input {...getHeaderInputProps()} />
          {formData.headerMediaUrl ? (
            <Box>
              <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>
                Change Banner
              </Typography>
            </Box>
          ) : (
            <Box>
              <CloudUpload sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>
                {isHeaderDragActive ? 'Drop banner here...' : 'Upload Banner'}
              </Typography>
            </Box>
          )}
        </MediaUploadZoneMobile>
        {formData.headerMediaUrl && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <img src={formData.headerMediaUrl} alt="Header Preview" style={{ maxWidth: '100%', borderRadius: '8px' }} />
          </Box>
        )}
      </Box>
    </Stack>
  );

  const renderDesignSettings = () => (
    <Stack spacing={3} sx={{ p: 2 }}>
      <Typography variant="h6" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ViewModule color="primary" />
        Design & Layout
      </Typography>

      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          Layout Style
        </Typography>
        <Stack direction="row" spacing={2}>
          <ModernButton
            variant={formData.viewType === 'SIMPLE' ? 'contained' : 'outlined'}
            startIcon={<ViewModule />}
            onClick={() => updateField('viewType', 'SIMPLE')}
            bgcolor={formData.colorScheme.main}
            fullWidth
          >
            Simple
          </ModernButton>
          <ModernButton
            variant={formData.viewType === 'COMPLEX' ? 'contained' : 'outlined'}
            startIcon={<ViewQuilt />}
            onClick={() => updateField('viewType', 'COMPLEX')}
            bgcolor={formData.colorScheme.main}
            fullWidth
          >
            Complex
          </ModernButton>
        </Stack>
        <FormHelperText>
          Simple: Clean grid layout | Complex: Rich cards with details
        </FormHelperText>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          Color Templates
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {COLOR_TEMPLATES.map((template, index) => (
            <ColorTemplate
              key={template.name}
              isSelected={selectedTemplate === index}
              onClick={() => applyColorTemplate(template, index)}
              sx={{
                background: `linear-gradient(135deg, ${template.main} 0%, ${template.accent} 100%)`,
              }}
            />
          ))}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          Animations
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={formData.animationsEnabled}
              onChange={(e) => updateField('animationsEnabled', e.target.checked)}
            />
          }
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <Animation />
              <Typography>Enable Animations</Typography>
            </Box>
          }
        />
        {formData.animationsEnabled && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Animation Style</InputLabel>
            <Select
              value={formData.animationType}
              label="Animation Style"
              onChange={(e) => updateField('animationType', e.target.value)}
              sx={{ borderRadius: '12px' }}
            >
              {ANIMATION_TYPES.map((anim) => (
                <MenuItem key={anim.value} value={anim.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>{anim.icon}</span>
                    <Typography>{anim.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{ANIMATION_TYPES.find((anim) => anim.value === formData.animationType)?.description}</FormHelperText>
          </FormControl>
        )}
      </Box>
    </Stack>
  );

  const renderAdvancedSettings = () => (
    <Stack spacing={3} sx={{ p: 2 }}>
      <Typography variant="h6" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Settings color="primary" />
        Privacy & Features
      </Typography>

      <Stack spacing={2}>
        <FormControlLabel
          control={<Switch checked={formData.isPublic} onChange={(e) => updateField('isPublic', e.target.checked)} />}
          label={
            <Box display="flex" alignItems="center" gap={1}>
              {formData.isPublic ? <Public /> : <PublicOff />}
              <Typography>Public Roster</Typography>
            </Box>
          }
        />

        <FormControlLabel
          control={<Switch checked={formData.qrEnabled} onChange={(e) => updateField('qrEnabled', e.target.checked)} />}
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <QrCode />
              <Typography>QR Code Sharing</Typography>
            </Box>
          }
        />

        <FormControlLabel
          control={<Switch checked={formData.showStats} onChange={(e) => updateField('showStats', e.target.checked)} />}
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUp />
              <Typography>Show Creator Statistics</Typography>
            </Box>
          }
        />

        <FormControlLabel
          control={<Switch checked={formData.showContactInfo} onChange={(e) => updateField('showContactInfo', e.target.checked)} />}
          label={
            <Box display="flex" alignItems="center" gap={1}>
              {formData.showContactInfo ? <Visibility /> : <VisibilityOff />}
              <Typography>Show Contact Information</Typography>
            </Box>
          }
        />
      </Stack>

      <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ mt: 3 }}>
        SEO Settings
      </Typography>

      <TextField
        label="SEO Title"
        value={formData.seoTitle}
        onChange={(e) => updateField('seoTitle', e.target.value)}
        fullWidth
        helperText="Custom page title for search engines"
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      />

      <TextField
        label="SEO Description"
        value={formData.seoDescription}
        onChange={(e) => updateField('seoDescription', e.target.value)}
        fullWidth
        multiline
        rows={2}
        helperText="Description that appears in search results"
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      />
    </Stack>
  );

  const renderPreview = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Preview color="primary" />
        Live Preview
      </Typography>

      <MobileCard sx={{ bgcolor: formData.colorScheme.backgroundColor }}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${formData.colorScheme.main} 0%, ${formData.colorScheme.accent} 100%)`,
            p: 3,
            color: 'white',
            textAlign: 'center',
            borderRadius: '16px 16px 0 0',
          }}
        >
          {formData.logo && (
            <Avatar src={formData.logo} sx={{ width: 60, height: 60, mx: 'auto', mb: 2 }} />
          )}
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {formData.name || 'Your Roster Name'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {formData.description || 'Your roster description will appear here'}
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Our Creators
          </Typography>
          <Stack spacing={2}>
            {[{ name: '@creator1', followers: '100K', platform: 'Sample', avatar: 'C' }].map((creator, index) => (
              <MobileCard key={index}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: formData.colorScheme.main }}>
                      {creator.avatar}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {creator.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {creator.platform} • {formData.showStats ? creator.followers : 'Content Creator'}
                      </Typography>
                    </Box>
                    {formData.showStats && (
                      <Chip
                        label="Active"
                        size="small"
                        sx={{
                          bgcolor: alpha(formData.colorScheme.accent, 0.3),
                          color: formData.colorScheme.main,
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </MobileCard>
            ))}
          </Stack>
        </Box>
      </MobileCard>

      <Alert severity="info" sx={{ mt: 2, borderRadius: '12px' }}>
        <Typography variant="body2">
          This is a preview. Actual creator data will be displayed on the live roster.
        </Typography>
      </Alert>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="primary">
          Loading Roster Settings
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        pt: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(1)})`,
        pb: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(10)})`,
        overflowY: 'auto',
      }}
    >
      <Paper elevation={0} sx={{ borderRadius: 0, mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h5" component="h1" fontWeight="bold">
              Roster Settings
            </Typography>
            <Box display="flex" gap={1}>
              {hasChanges && (
                <Fade in={hasChanges}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={createMutation.isLoading || updateMutation.isLoading ? <LinearProgress size={16} /> : <Check />}
                    onClick={handleSave}
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    sx={{
                      borderRadius: '20px',
                      textTransform: 'none',
                      bgcolor: 'success.main',
                      '&:hover': { bgcolor: 'success.dark' },
                    }}
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save'}
                  </Button>
                </Fade>
              )}
              <IconButton size="small" color="primary">
                <HelpOutline />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Customize your creator roster
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Chip
              icon={formData.isPublic ? <Public /> : <PublicOff />}
              label={formData.isPublic ? 'Public' : 'Private'}
              color={formData.isPublic ? 'success' : 'default'}
              size="small"
            />
            {formData.animationsEnabled && (
              <Chip
                icon={<Animation />}
                label={formData.animationType.replace('_', ' ')}
                color="secondary"
                size="small"
              />
            )}
            <Chip
              label={formData.viewType === 'SIMPLE' ? 'Simple Layout' : 'Complex Layout'}
              variant="outlined"
              size="small"
            />
            {formData.qrEnabled && (
              <Chip
                icon={<QrCode />}
                label="QR Enabled"
                color="primary"
                size="small"
              />
            )}
          </Stack>
        </Box>
      </Paper>

      {successMsg && (
        <Alert severity="success" sx={{ m: 2, borderRadius: '12px' }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}
      {errorMsg && (
        <Alert severity="error" sx={{ m: 2, borderRadius: '12px' }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}

      <Paper sx={{ position: 'sticky', top: 0, zIndex: 100, borderRadius: 0 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Basic" icon={<Info />} iconPosition="start" />
          <Tab label="Design" icon={<ViewModule />} iconPosition="start" />
          <Tab label="Advanced" icon={<Settings />} iconPosition="start" />
          <Tab label="Preview" icon={<Preview />} iconPosition="start" />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {renderBasicSettings()}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {renderDesignSettings()}
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {renderAdvancedSettings()}
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        {renderPreview()}
      </TabPanel>

      {/* SpeedDial positioned ABOVE AppShell (86px + 12px margin = 98px from bottom) */}
      <SpeedDial
        ariaLabel="Roster Actions"
        sx={{
          position: 'fixed',
          bottom: `calc(env(safe-area-inset-bottom, 0px) + 110px)`, // Above the 86px AppShell + extra space
          right: 16,
        }}
        icon={<SpeedDialIcon />}
        open={speedDialOpen}
        onClose={() => setSpeedDialOpen(false)}
        onOpen={() => setSpeedDialOpen(true)}
      >
        <SpeedDialAction
          key="preview"
          icon={<Preview />}
          tooltipTitle="Full Preview"
          onClick={() => {
            setIsPreviewOpen(true);
            setSpeedDialOpen(false);
          }}
        />
      </SpeedDial>

      <Dialog
        fullScreen
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        sx={{ '& .MuiDialog-paper': { bgcolor: formData.colorScheme.backgroundColor } }}
      >
        <AppBar sx={{ position: 'relative', bgcolor: formData.colorScheme.main }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => setIsPreviewOpen(false)}>
              <ArrowBack />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              Roster Preview
            </Typography>
            <Typography variant="body2">
              /roster/{formData.slug}
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {renderPreview()}
        </Box>
      </Dialog>
    </Box>
  );
}